'use client';

import { ButtonProps, Spinner } from '@ui';
import Dropdown, { DropdownPlacement } from '@ui/Dropdown';
import { cx } from 'class-variance-authority';
import { useRouter as usePagesRouter } from 'next/compat/router';
import { useRouter as useAppRouter, useParams, usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadUser } from 'redux/actions/authActions';
import { AppState } from 'redux/store';
import { useApi } from '../../hooks/useApi';
import { locales } from './locales';

interface Props extends ButtonProps {
    full?: boolean;
    placement?: DropdownPlacement;
}

const useLocaleRouter = () => {
    const pagesRouter = usePagesRouter();
    const appRouter = useAppRouter();
    const pathname = usePathname();
    const params = useParams();

    const isPages = !!pagesRouter;

    const currentLocaleCode = isPages
        ? pagesRouter.locale
        : (params?.locale as string) || (params?.lang as string) || 'de';

    const pushLocale = (newLocale: string) => {
        if (isPages && pagesRouter) {
            const { pathname, asPath, query } = pagesRouter;
            pagesRouter.push({ pathname, query }, asPath, { locale: newLocale });
        } else {
            if (!pathname) return;

            const segments = pathname.split('/');
            if (locales[segments[1]]) {
                segments[1] = newLocale;
            } else {
                segments.splice(1, 0, newLocale);
            }
            const newPath = segments.join('/');
            appRouter.push(newPath);
        }
    };

    return { locale: currentLocaleCode, pushLocale };
};

export const LanguageDropdown = ({ full, placement, ...props }: Props) => {
    const api = useApi();
    const dispatch = useDispatch();

    const { locale, pushLocale } = useLocaleRouter();

    const { isAuthenticated, user, isLoading } = useSelector((state: AppState) => state.auth);

    const currentLocale = useMemo(() => {
        const activeLocale = locale || user?.locale || 'de';
        return locales[activeLocale] ? locales[activeLocale] : locales['de'];
    }, [user, locale]);

    const changeLanguage = (newLocale: string) => {
        if (isAuthenticated) {
            api({
                url: 'user/locale/' + newLocale,
                onSuccess: () => {
                    dispatch(loadUser());
                    pushLocale(newLocale);
                }
            });
        } else {
            pushLocale(newLocale);
        }
    };

    useEffect(() => {
        if (isAuthenticated && user?.locale && locale && user.locale !== locale) {
            pushLocale(user.locale);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.locale, locale, isAuthenticated]);

    return (
        <Dropdown
            placement={placement}
            className="tw-w-full"
            size="sm"
            flat
            label={
                isLoading ? (
                    <Spinner color="currentColor" size="xs" />
                ) : (
                    <>
                        <span
                            style={{ backgroundImage: `url('/images/flags/${currentLocale.flag}.svg')` }}
                            className={cx('fi', full && 'tw-mr-3')}
                        />
                        {full ? `${currentLocale.label}` : ''}
                    </>
                )
            }
        >
            {Object.entries(locales).map(([key, localeData]: [string, any]) => (
                <Dropdown.Item
                    key={key}
                    onClick={() => changeLanguage(key)}
                    label={localeData.label}
                    icon={
                        <span
                            style={{ backgroundImage: `url('/images/flags/${localeData.flag}.svg')` }}
                            className={`fi`}
                        />
                    }
                />
            ))}
        </Dropdown>
    );
};
